import { useEffect, useRef, useState } from 'react';
import type {
  DiagnosticInput,
  FirstHourSignal,
  NetworkSize,
  PostFormat,
  PostingWindow,
} from '@/lib/linkedinDiagnostic';
import { diagnose } from '@/lib/linkedinDiagnostic';

type AnswerKey = keyof DiagnosticInput;

interface Option {
  value: string;
  label: string;
  muted?: boolean;
}

interface Step {
  key: AnswerKey;
  question: string;
  options: Option[];
}

const STEPS: Step[] = [
  {
    key: 'format',
    question: 'Quel est le format de votre post ?',
    options: [
      { value: 'text', label: 'Texte seul' },
      { value: 'image', label: 'Image' },
      { value: 'carousel', label: 'Carrousel' },
      { value: 'video', label: 'Vidéo' },
    ],
  },
  {
    key: 'window',
    question: 'Quand l’avez-vous publié ?',
    options: [
      { value: 'weekday_midday', label: 'Mardi, mercredi ou jeudi, entre 11h et 17h' },
      { value: 'weekday_other', label: 'Un autre jour ou horaire de semaine' },
      { value: 'weekend', label: 'Le week-end' },
      { value: 'unknown', label: 'Je ne sais plus', muted: true },
    ],
  },
  {
    key: 'network',
    question: 'Combien de relations avez-vous sur LinkedIn ?',
    options: [
      { value: 'small', label: 'Moins de 500' },
      { value: 'medium', label: 'Entre 500 et 2000' },
      { value: 'large', label: 'Plus de 2000' },
      { value: 'unknown', label: 'Je ne sais pas', muted: true },
    ],
  },
  {
    key: 'signal',
    question: 'Combien de réactions aviez-vous dans la première heure ?',
    options: [
      { value: 'zero', label: 'Aucune' },
      { value: 'few', label: '1 ou 2' },
      { value: 'several', label: '3 ou plus' },
      { value: 'unknown', label: 'Je ne me souviens plus', muted: true },
    ],
  },
];

type Answers = Partial<Record<AnswerKey, string>>;

export function DiagnosticWizard() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Answers>({});
  const [copied, setCopied] = useState(false);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const isMounted = useRef(false);

  // L'archétype « wizard » veut que le focus suive l'étape : un lecteur
  // d'écran doit entendre la nouvelle question sans avoir à re-chercher où il en est.
  // Pas au chargement : la page sauterait au widget en passant par-dessus le H1.
  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    headingRef.current?.focus();
  }, [step]);

  const isDone = step >= STEPS.length;

  const answer = (key: AnswerKey, value: string) => {
    setAnswers((current) => ({ ...current, [key]: value }));
    setStep((current) => current + 1);
  };

  const restart = () => {
    setAnswers({});
    setCopied(false);
    setStep(0);
  };

  if (isDone) {
    const input: DiagnosticInput = {
      format: (answers.format as PostFormat) ?? 'text',
      window: (answers.window as PostingWindow) ?? 'unknown',
      network: (answers.network as NetworkSize) ?? 'unknown',
      signal: (answers.signal as FirstHourSignal) ?? 'unknown',
    };
    const result = diagnose(input);

    const copyResult = () => {
      const text = [result.headline, ...result.paragraphs, result.tip].join('\n\n');
      try {
        navigator.clipboard
          .writeText(text)
          .then(() => setCopied(true))
          .catch(() => {
            // Écriture refusée par le navigateur : le bouton reste silencieusement inerte.
          });
      } catch {
        // Pas de Clipboard API (navigateur ancien, contexte non sécurisé) : on n'interrompt rien.
      }
    };

    return (
      <div className="nz-wizard">
        <div className="nz-wizard__result">
          <h2 className="nz-wizard__result-headline" tabIndex={-1} ref={headingRef}>
            {result.headline}
          </h2>
          {result.paragraphs.map((paragraph, index) => (
            <p key={index}>{paragraph}</p>
          ))}
          <div className="nz-wizard__tip">{result.tip}</div>
          <div className="nz-wizard__actions">
            <button type="button" className="nz-btn-secondary" onClick={copyResult}>
              {copied ? 'Copié' : 'Copier le diagnostic'}
            </button>
            <button type="button" className="nz-wizard__back" onClick={restart}>
              Recommencer
            </button>
          </div>
        </div>
      </div>
    );
  }

  const current = STEPS[step];
  const progress = Math.round((step / STEPS.length) * 100);

  return (
    <div className="nz-wizard">
      <div
        className="nz-wizard__progress"
        role="progressbar"
        aria-valuenow={progress}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Progression du diagnostic"
      >
        <div className="nz-wizard__bar" style={{ width: `${progress}%` }} />
      </div>

      <div className="nz-wizard__step-label">
        Question {step + 1} sur {STEPS.length}
      </div>
      <h2 className="nz-wizard__question" tabIndex={-1} ref={headingRef}>
        {current.question}
      </h2>

      <div className="nz-wizard__options">
        {current.options.map((option) => (
          <button
            key={option.value}
            type="button"
            className={`nz-wizard__option${option.muted ? ' nz-wizard__option--muted' : ''}`}
            onClick={() => answer(current.key, option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>

      {step > 0 ? (
        <button type="button" className="nz-wizard__back" onClick={() => setStep((current) => current - 1)}>
          ← Retour
        </button>
      ) : null}
    </div>
  );
}
